import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import UpdateCompany from './UpdateCompany';
import UpdateLicense from './UpdateLicense';
import config from '../../config';
import { isServiceVendor, isRoot } from '../../lib/tools';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Contents,
  Header,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import {
  Dialog,
  TabDetail,
  Tabs,
  Tab,
  SubmitWrapper,
  BaseButton,
  Operation,
} from './styles/UpdateCompanyDialogStyles';
import { createCompanyData } from './functions/updateCompanyDialogUtils';

const TAB = {
  COMPANY: 1,
  LICENSE: 2,
};
const {
  deleteCompany,
  updateCompany,
  deleteDataAll,
  updateLicense,
  displayLicense,
} = config.customRole.functionAccess;

const UpdateCompanyDialog = ({
  companyTab,
  role,
  currentCompanyInfo,
  confirmData,
  confirmDataError,
  onUpdateLicense,
  onSelectTab,
  onClose,
  onUpdate,
  onDelete,
  onDataDelete,
  onUpdateConfirm,
  onSearchLatLng,
}) => {
  const canDeleteCompany =
    (isServiceVendor(role) && currentCompanyInfo.companyType === 'customer') ||
    isRoot(role);
  const displayDeleteCompany =
    deleteCompany.indexOf(role) !== -1 && canDeleteCompany;
  const displayUpdateCompany = updateCompany.indexOf(role) !== -1;
  const displayDeleteDataAll =
    deleteDataAll.indexOf(role) !== -1 && canDeleteCompany;
  const displayTabLicense =
    !(isServiceVendor(role) && currentCompanyInfo.companyType === 'provider') &&
    !isRoot(role);
  const enableUpdateLicense = updateLicense.indexOf(role) !== -1;
  const enableDisplayLicense = displayLicense.indexOf(role) !== -1;

  // 更新ボタンクリック時の処理
  const handleUpdateButtonClick = () => {
    const companyData = createCompanyData(
      confirmData,
      confirmDataError,
      enableUpdateLicense,
      enableDisplayLicense,
    );
    onUpdate(companyData);
    onClose();
  };

  return (
    <Main
      id="closeConfirmCompanyWindow"
      role="presentation"
      onClick={() => onClose()}
    >
      <Dialog role="presentation" onClick={e => e.stopPropagation()}>
        <Contents>
          <Header>
            <FormattedMessage id="company.UpdateCompany.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onClose()}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          {displayTabLicense && (
            <Tabs>
              <Tab
                id="selectCompanyTab"
                role="presentation"
                onClick={() => onSelectTab(TAB.COMPANY)}
                current={companyTab === TAB.COMPANY}
              >
                <FormattedMessage id="company.UpdateCompanyDialog.tab.company" />
              </Tab>
              <Tab
                id="selectLicenseTab"
                role="presentation"
                onClick={() => onSelectTab(TAB.LICENSE)}
                current={companyTab === TAB.LICENSE}
              >
                <FormattedMessage id="company.UpdateCompanyDialog.tab.license" />
              </Tab>
            </Tabs>
          )}
          {companyTab === TAB.COMPANY && (
            <TabDetail>
              <UpdateCompany
                currentCompanyInfo={currentCompanyInfo}
                confirmData={confirmData}
                confirmDataError={confirmDataError}
                onUpdateConfirm={onUpdateConfirm}
                onSearchLatLng={onSearchLatLng}
              />
            </TabDetail>
          )}
          {companyTab === TAB.LICENSE && (
            <TabDetail>
              <UpdateLicense
                role={role}
                displayUpdateCompany={displayUpdateCompany}
                currentCompanyInfo={currentCompanyInfo}
                confirmData={confirmData}
                confirmDataError={confirmDataError}
                onChange={(target, params) => {
                  onUpdateLicense(target, params);
                }}
              />
            </TabDetail>
          )}
          {/* 更新ボタン */}
          <Operation>
            {displayUpdateCompany && (
              <SubmitWrapper>
                <BaseButton
                  id="updateButtonClick"
                  role="presentation"
                  className="button"
                  onClick={() => handleUpdateButtonClick()}
                >
                  <FormattedMessage id="company.UpdateCompany.button.update" />
                </BaseButton>
              </SubmitWrapper>
            )}
            {displayDeleteDataAll && (
              <SubmitWrapper>
                <BaseButton
                  id="dataDeleteButtonClick"
                  role="presentation"
                  className="button"
                  onClick={() => {
                    const param = {
                      disableType: '1',
                      companyIds: [confirmData.companyId],
                    };
                    onDataDelete(param);
                  }}
                  btnType="delete"
                >
                  <FormattedMessage id="company.UpdateCompany.button.dataDelete" />
                </BaseButton>
              </SubmitWrapper>
            )}
            {displayDeleteCompany && (
              <SubmitWrapper>
                <BaseButton
                  id="deleteButtonClick"
                  role="presentation"
                  className="button"
                  onClick={() => {
                    const companies = [
                      {
                        id: confirmData.companyId,
                        delete: true,
                      },
                    ];
                    onDelete(companies);
                  }}
                  btnType="delete"
                >
                  <FormattedMessage id="company.UpdateCompany.button.delete" />
                </BaseButton>
              </SubmitWrapper>
            )}
          </Operation>
        </Contents>
      </Dialog>
    </Main>
  );
};

UpdateCompanyDialog.propTypes = {
  companyTab: PropTypes.number.isRequired,
  role: PropTypes.number.isRequired,
  currentCompanyInfo: PropTypes.object.isRequired,
  confirmData: PropTypes.object.isRequired,
  confirmDataError: PropTypes.object.isRequired,
  onUpdateLicense: PropTypes.func.isRequired,
  onSelectTab: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDataDelete: PropTypes.func.isRequired,
  onUpdateConfirm: PropTypes.func.isRequired,
  onSearchLatLng: PropTypes.func.isRequired,
};

export default UpdateCompanyDialog;
